import { type PolicyBranchesForBrokerEntity } from 'korint-sdk';
import type { KorintConfig, KorintModulesConfig } from 'korint-sdk/react';
import { AWAITING_FIRST_PAYMENT, STOP_SCHEDULED } from 'korint-sdk/react';
import { config } from './config';
import { garantiePlusAssistanceConfig } from './config/products/garantiePlusAssistance';
import { policyStatuses } from './constants';
import { defaultErrorMessage, errorDictionary } from './errors';
import dayjs from 'dayjs';

const korintModulesConfig = {
  policyholderContactsModuleConfig: {
    contacts: [
      {
        type: 'email',
        showIcon: true,
        value: 'contact@garantieplus.fr',
      },
      {
        type: 'email',
        showIcon: false,
        label: 'Pour modifier vos informations',
        value: 'adv@garantieplus.fr',
      },
      {
        type: 'email',
        showIcon: false,
        label: 'Pour déclarer un changement de propriétaire du véhicule',
        prefix: 'Téléchargez le document de changement et envoyez-le rempli et signé à ',
        value: 'adv@garantieplus.fr',
      },
      {
        type: 'email',
        showIcon: false,
        label: 'Pour déclarer un sinistre',
        value: 'sinistre@garantieplus.fr',
      },
    ],
  },
  methodPayment: {
    stripePublishableKey: config.STRIPE_PUBLISHABLE_KEY,
  },
  intermediaryUserCreation: {
    inviteRole: {
      WHOLESALE: ['WHOLESALE_COMMERCIAL'],
      RETAIL: ['RETAIL_COMMERCIAL'],
    },
  },
  ongoingNewBusiness: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: ['POLICY_CREATED', 'POLICY_QUOTED', 'POLICY_CONFIRMED'],
      },
      brokerageFirmName: {
        type: 'textInput',
      },
      creationDate: {
        type: 'date',
      },
    },
    baseFilters: { viewType: 'newBusiness' },
    initialFilters: [
      {
        field: 'creationDate',
        value: {
          start: dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
        },
      },
    ],
    expandAssets: true,
    expandCustomers: true,
    expandBrokerageFirm: true,
    columns: [
      {
        name: 'lastName',
        type: 'string',
        accessor: ({ customers }: PolicyBranchesForBrokerEntity) =>
          customers?.[0]?.customFields?.find(field => field.key === 'lastName')?.value as
            | string
            | undefined,
      },
      {
        name: 'firstName',
        type: 'string',
        accessor: ({ customers }: PolicyBranchesForBrokerEntity) =>
          customers?.[0]?.customFields?.find(field => field.key === 'firstName')?.value as
            | string
            | undefined,
      },
      {
        name: 'registrationNumber',
        type: 'string',
        accessor: ({ assets }: PolicyBranchesForBrokerEntity) =>
          assets?.[0]?.customFields?.find(field => field.key === 'registrationNumber')?.value as
            | string
            | undefined,
      },
      {
        name: 'brand',
        type: 'string',
        accessor: ({ assets }: PolicyBranchesForBrokerEntity) =>
          assets?.[0]?.customFields?.find(field => field.key === 'brand')?.value as
            | string
            | undefined,
      },
      {
        name: 'externalId',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) => policy?.externalId,
      },
      {
        name: 'author',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) =>
          policy.author?.email ||
          (policy.author?.firstName &&
            policy.author?.lastName &&
            `${policy.author?.firstName} ${policy.author?.lastName}`),
      },
      {
        name: 'brokerageFirmName',
        type: 'string',
        accessor: ({ brokerageFirm }: PolicyBranchesForBrokerEntity) =>
          (brokerageFirm?.customFields?.find(field => field.key === 'garageName')?.value as
            | string
            | undefined) ??
          brokerageFirm?.companyInfo?.name ??
          '',
      },
      {
        name: 'status',
        type: 'status',
        statuses: policyStatuses,
        path: 'policy.status',
      },
      {
        name: 'createdAt',
        type: 'date',
        format: 'datetime',
        path: 'branch.createdAt',
      },
    ],
  },
  portfolio: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: [
          'POLICY_SIGNED',
          'POLICY_STARTED',
          'POLICY_STOPPED',
          'POLICY_SUSPENDED',
          STOP_SCHEDULED,
          AWAITING_FIRST_PAYMENT,
        ],
      },
      endDate: {
        type: 'date',
      },
      creationDate: {
        type: 'date',
      },
      brokerageFirmName: {
        type: 'textInput',
      },
      stopReason: {
        type: 'select',
        options: [
          'DEROGATION_DENIED',
          'VEHICLE_TAKEN_BACK_BY_PROFESSIONAL',
          'BREACH_OF_CONTRACT_CLAUSES',
          'DUPLICATE_CONTRACT',
          'END_OF_CONTRACT',
        ],
      },
    },
    baseFilters: { viewType: 'live', order: { by: 'signedAt', direction: 'desc' } },
    expandAssets: true,
    expandCustomers: true,
    expandBrokerageFirm: true,
    columns: [
      {
        name: 'lastName',
        type: 'string',
        accessor: ({ customers }: PolicyBranchesForBrokerEntity) =>
          customers?.[0]?.customFields?.find(field => field.key === 'lastName')?.value as
            | string
            | undefined,
      },
      {
        name: 'firstName',
        type: 'string',
        accessor: ({ customers }: PolicyBranchesForBrokerEntity) =>
          customers?.[0]?.customFields?.find(field => field.key === 'firstName')?.value as
            | string
            | undefined,
      },
      {
        name: 'registrationNumber',
        type: 'string',
        accessor: ({ assets }: PolicyBranchesForBrokerEntity) =>
          assets?.[0]?.customFields?.find(field => field.key === 'registrationNumber')?.value as
            | string
            | undefined,
      },
      {
        name: 'brand',
        type: 'string',
        accessor: ({ assets }: PolicyBranchesForBrokerEntity) =>
          assets?.[0]?.customFields?.find(field => field.key === 'brand')?.value as
            | string
            | undefined,
      },
      {
        name: 'externalId',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) => policy?.externalId,
      },
      {
        name: 'author',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) =>
          policy.author?.email ||
          (policy.author?.firstName &&
            policy.author?.lastName &&
            `${policy.author?.firstName} ${policy.author?.lastName}`),
      },
      {
        name: 'brokerageFirmName',
        type: 'string',
        accessor: ({ brokerageFirm }: PolicyBranchesForBrokerEntity) =>
          (brokerageFirm?.customFields?.find(field => field.key === 'garageName')?.value as
            | string
            | undefined) ??
          brokerageFirm?.companyInfo?.name ??
          '',
      },
      {
        name: 'status',
        type: 'status',
        statuses: policyStatuses,
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) => {
          if (
            policy.status === 'POLICY_STOPPED' &&
            (policy as { stop?: { reason?: string } }).stop?.reason === 'END_OF_CONTRACT'
          ) {
            return 'POLICY_TERMINATED';
          }
          return policy.status;
        },
      },
      {
        name: 'signedAt',
        type: 'date',
        format: 'datetime',
        path: 'policy.signedAt',
      },
      {
        name: 'createdAt',
        type: 'date',
        format: 'datetime',
        path: 'policy.createdAt',
      },
    ],
  },
  accountingDashboard: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: ['POLICY_SIGNED', 'POLICY_STARTED', 'POLICY_STOPPED', 'POLICY_SUSPENDED'],
      },
      terminationReason: {
        type: 'select',
        options: [
          'DEROGATION_DENIED',
          'VEHICLE_TAKEN_BACK_BY_PROFESSIONAL',
          'BREACH_OF_CONTRACT_CLAUSES',
          'DUPLICATE_CONTRACT',
          'END_OF_CONTRACT',
        ],
      },
    },
  },
  policyLifecycleSummaryModuleConfig: {
    dateDisplayFormat: 'DD/MM/YYYY',
  },
  businessRolesConfig: {
    RETAIL_COMMERCIAL: {
      redirectRules: {
        hasUserProducts: '/portfolio',
        hasPendingDistributionRequest: '/waiting',
        noDistributionRequest: (brokerageFirmId: string) =>
          `/brokerage-firm/${brokerageFirmId}/information`,
        default: () => '/nobody',
      },
    },
    RETAIL_ADMIN: {
      redirectRules: {
        hasUserProducts: '/intermediary-users-list',
        hasPendingDistributionRequest: '/waiting',
        noDistributionRequest: (brokerageFirmId: string) =>
          `/brokerage-firm/${brokerageFirmId}/creation`,
        default: () => '/nobody',
      },
    },
    WHOLESALE_COMMERCIAL: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
    WHOLESALE_ADMIN: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
    DRIVER: {
      redirectRules: {
        default: policyId => `/driver/${policyId}`,
      },
    },
    SUPER_ADMIN: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
  },
  intermediaryGeneralModule: {
    displayInscriptionLink: true,
  },
  distributionRequestListModule: {
    intermediaryAdditionalInformationFields: [],
  },
  documents: {
    uploadModes: {
      policyholderDocuments: 'batch',
      nonDelegatedDocuments: 'category',
      brokerageFirmDocuments: 'batch',
    },
  },
} satisfies KorintModulesConfig;

const korintConfig = {
  modulesConfig: korintModulesConfig,
  policyStatuses,
  getPolicyStatusOverride: (status, stopReason) => {
    if (status === 'POLICY_STOPPED' && stopReason === 'END_OF_CONTRACT') {
      return 'POLICY_TERMINATED';
    }
    return undefined;
  },
  dataAccessors: {},
  productsConfig: {
    garantiePlusAssistance: garantiePlusAssistanceConfig,
  },
  errorsConfig: {
    // It is used only in uniq, it it useless here
    defaultErrorMessage: defaultErrorMessage,
    errorDictionary: errorDictionary,
  },
  supportLinkRoles: ['WHOLESALE_COMMERCIAL', 'WHOLESALE_ADMIN'],
} as const satisfies KorintConfig;

export default korintConfig;
